import { classnames } from '@/helpers/general';
import { customTwMerge } from '@/helpers/theme';
import type { RatingV2Props } from './RatingV2';

// Heights follow the line heights of the score and count text in RatingV2; widths approximate a typical score and count.
const SCORE_SKELETON_CLASS = { sm: 'h-[18px] w-[22px]', md: 'h-lg w-[26px]', lg: 'h-lg w-[26px]' } as const;
const COUNT_SKELETON_CLASS = { sm: 'h-[18px] w-[96px]', md: 'h-[18px] w-[96px]', lg: 'h-lg w-[112px]' } as const;

type SkeletonProps = Pick<
  RatingV2Props,
  'size' | 'direction' | 'withAverageScore' | 'withRatingCounter' | 'withMultipleStars' | 'className'
>;

/** Takes the same layout props as RatingV2 and lays out placeholders where it would draw. */
export const RatingV2Skeleton = ({
  size,
  direction = 'row',
  withAverageScore,
  withRatingCounter,
  withMultipleStars = true,
  className,
}: SkeletonProps) => {
  const gradientBase = 'bg-surface_l3 animate-pulse rounded';

  const score = withAverageScore && <span className={classnames(gradientBase, SCORE_SKELETON_CLASS[size])} />;

  const star = (key?: number) => <span key={key} className={classnames(gradientBase, 'h-md w-md')} />;
  const stars = withMultipleStars ? <span className="gap-2xs flex">{[1, 2, 3, 4, 5].map(star)}</span> : star();

  const count = withRatingCounter && <span className={classnames(gradientBase, COUNT_SKELETON_CLASS[size])} />;

  const scoreAndStars = (
    <span className="gap-2xs flex items-center">
      {direction === 'row' && !withMultipleStars ? (
        <>
          {stars}
          {score}
        </>
      ) : (
        <>
          {score}
          {stars}
        </>
      )}
    </span>
  );

  if (direction === 'column') {
    return (
      <div aria-hidden="true" className={customTwMerge(classnames('gap-2xs flex flex-col items-end', className))}>
        {scoreAndStars}
        {count}
      </div>
    );
  }

  return (
    <div aria-hidden="true" className={customTwMerge(classnames('gap-xs flex items-center', className))}>
      {scoreAndStars}
      {count && (
        <>
          <span className="h-xxs w-xxs bg-surface_l3 rounded-full" />
          {count}
        </>
      )}
    </div>
  );
};
